/**
 * CheckinTaskRows — the check-ins and form sends a family owes TRF, as to-do
 * rows on Today (wave w1023c L1, Victor 2026-10-23: "Check-ins move to Today").
 *
 *   [kind] title                                  [Answer]
 *          Due Tue, Oct 27
 *
 * One row per open, unanswered form send (`openCheckinTasks`). "Answer" opens
 * the SAME answer flow the Community feed uses — `PollCard`, with the same
 * vote call — in a dialog, so nothing about answering is re-implemented here.
 * After a vote the feed key is invalidated; the row leaves the list because
 * the feed now says `voted`, and the dialog stays open on PollCard's own
 * "thank you" state until the family closes it.
 *
 * This is a list of to-dos, not a banner (DECISIONS: one transient block above
 * the composer). It renders nothing when nothing is owed.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardCheck } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { MONO_LABEL_TYPE, PendingDecision } from '@/components/editorial';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import PollCard from '@/components/community/PollCard';
import { api } from '@/services/api';
import { cn } from '@/lib/utils';
import type { PollFeedItem, VoteAnswer } from '@/types/polls';
import { POLLS_FEED_QUERY_KEY, checkinDueDay } from './checkinTasks';

export interface CheckinTaskRowsProps {
  /** Already filtered and sorted by `openCheckinTasks`. */
  items: PollFeedItem[];
}

export function CheckinTaskRows({ items }: CheckinTaskRowsProps) {
  const { t } = useTranslation('dashboard');
  const queryClient = useQueryClient();
  // The item being answered is held here, not looked up in `items`: once the
  // vote lands the row leaves `items`, and the dialog must stay on PollCard's
  // thank-you state until the family closes it.
  const [answering, setAnswering] = useState<PollFeedItem | null>(null);

  if (items.length === 0 && !answering) return null;

  const vote = (pollId: string, answers: VoteAnswer[]) =>
    api.polls.vote(pollId, answers).then(() => {
      // Reader: this list and Today's polls count (DashboardPage), and the
      // Community feed — all three read POLLS_FEED_QUERY_KEY.
      void queryClient.invalidateQueries({ queryKey: POLLS_FEED_QUERY_KEY });
    });

  return (
    <section aria-labelledby="checkin-tasks-heading" data-testid="checkin-tasks">
      {items.length > 0 && (
        <>
          <h2
            id="checkin-tasks-heading"
            className={cn(MONO_LABEL_TYPE, 'mb-1 flex items-center gap-1 text-stevens-ink-3')}
          >
            {t('checkins.heading')}
            <PendingDecision id="checkin-custom-on-today" side="bottom" />
          </h2>
          <ul className="divide-y divide-border/60">
            {items.map((it) => {
              const due = checkinDueDay(it.poll.end_at);
              const kind = it.poll.form_kind === 'checkin' ? t('checkins.kind.checkin') : t('checkins.kind.custom');
              return (
                <li
                  key={it.poll._id}
                  data-testid="checkin-task-row"
                  data-poll-id={it.poll._id}
                  className="flex items-center gap-3 py-2.5"
                >
                  <ClipboardCheck className="h-4 w-4 shrink-0 text-stevens-ink-3" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-body font-medium text-foreground">{it.poll.title}</p>
                    <p className="flex flex-wrap items-center gap-x-1.5 text-caption text-stevens-ink-3">
                      <span>{kind}</span>
                      {due && (
                        <>
                          <span aria-hidden className="text-border">·</span>
                          <span>{t('checkins.due', { day: due })}</span>
                        </>
                      )}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAnswering(it)}
                    aria-label={t('checkins.answerAria', { title: it.poll.title })}
                    className="inline-flex h-9 shrink-0 items-center rounded-md bg-stevens-indigo-solid px-3.5 text-body font-medium text-stevens-indigo-solid-fg hover:bg-stevens-indigo-solid-2 [@media(pointer:coarse)]:min-h-11"
                  >
                    {t('checkins.answer')}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}

      <Dialog open={answering !== null} onOpenChange={(open) => { if (!open) setAnswering(null); }}>
        <DialogContent className="max-w-2xl max-h-[85dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{answering?.poll.title ?? ''}</DialogTitle>
            <DialogDescription>{t('checkins.dialogDescription')}</DialogDescription>
          </DialogHeader>
          {answering && (
            <PollCard
              key={answering.poll._id}
              item={answering}
              onVote={(answers) => vote(answering.poll._id, answers)}
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
